import React, { useContext, useState, FormEvent } from "react";
import Drawer from "./Drawer";
import { RESTPost } from "../services/types";
import { UserContext } from "../contexts/UserContext";
import { toggleFacebookVote, toggleFacebookPromotion, addOrthoCorrection } from "../services/moderation";
import { trashPost, restorePost } from "../services/posts";
import { useAuthenticatedAction } from "../hooks/useAuthenticatedAction";
import Button from "./Button";
import AutoResizeTextarea from "./AutoResizeTextarea";
import CopyLinkButton from "./CopyLinkButton";
import "./ModerationDrawer.css";

interface ModerationDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    post: RESTPost;
    onVoteToggled?: (newCount: number) => void;
    onPromotionToggled?: (onFacebook: boolean) => void;
    onTrashToggled?: (isTrashed: boolean) => void;
    onOrthoCorrectionChanged?: (newOrthoCorrection: string) => void;
}

const ModerationDrawer: React.FC<ModerationDrawerProps> = ({
    isOpen,
    onClose,
    post,
    onVoteToggled,
    onPromotionToggled,
    onTrashToggled,
    onOrthoCorrectionChanged
}) => {
    const { currentUser, isModerator } = useContext(UserContext);
    
    // Only render for moderators
    if (!isModerator()) {
        return null;
    }
    const { execute: toggleVote, loading: voteLoading } = useAuthenticatedAction();
    const { execute: togglePromotion, loading: promotionLoading } = useAuthenticatedAction();
    const { execute: trashPostAction, loading: trashLoading } = useAuthenticatedAction();
    const { execute: addOrthoAction, loading: orthoLoading } = useAuthenticatedAction();
    
    // Local state for Facebook voters as objects {ID, displayName}
    const [localFacebookVoters, setLocalFacebookVoters] = useState<Array<{ID:number, displayName:string}>>(post.meta.facebook_votes || []);
    
    // Get Facebook voters data
    const currentUserHasVoted = currentUser ? localFacebookVoters.some(v => v.ID === currentUser.id) : false;
    const voterCount = localFacebookVoters.length;
    const [onFacebook, setOnFacebook] = useState<boolean>(Boolean(post.meta?.on_facebook));
    const [isTrashed, setIsTrashed] = useState<boolean>(post.status === 'trash');
    const [orthoCorrection, setOrthoCorrection] = useState<string>(post.meta.ortho_correction || '');
    const [orthoInput, setOrthoInput] = useState<string>('');
    const [showOrthoInput, setShowOrthoInput] = useState<boolean>(false);
    
    // Check if there's an ortho correction (use local state)
    const hasOrthoCorrection = () => {
        return orthoCorrection.trim() !== '';
    };
    
    const handleVoteForFacebook = async () => {
        if (!currentUser || voteLoading) return;
        
        const response = await toggleVote(toggleFacebookVote, post.id);
        
        if (response && response.success) {
            // Merge server voter names with local structure when possible
            const serverNames: string[] = response.voters || [];
            const byId = new Map(localFacebookVoters.map(v => [v.ID, v.displayName] as [number, string]));
            // If server provides names but not IDs, keep existing IDs and update names when matching current user
            if (currentUser) {
                const currentUserName = serverNames.find(n => n === currentUser.name) || currentUser.name;
                if (response.action === 'added') {
                    if (!localFacebookVoters.some(v => v.ID === currentUser.id)) {
                        setLocalFacebookVoters([{ ID: currentUser.id, displayName: currentUserName }, ...localFacebookVoters]);
                    } else {
                        setLocalFacebookVoters(localFacebookVoters.map(v => v.ID === currentUser.id ? { ...v, displayName: currentUserName } : v));
                    }
                } else if (response.action === 'removed') {
                    setLocalFacebookVoters(localFacebookVoters.filter(v => v.ID !== currentUser.id));
                }
            } else {
                // No current user, just keep existing voters
                setLocalFacebookVoters(localFacebookVoters);
            }
            // Notify parent of new count
            if (onVoteToggled) {
                onVoteToggled(response.vote_count);
            }
        }
    };

    const handleMoveToTrash = async () => {
        if (!currentUser || trashLoading) return;
        
        let response;
        if (isTrashed) {
            // Restore the post
            response = await trashPostAction(restorePost, post.id);
        } else {
            // Trash the post
            response = await trashPostAction(trashPost, post.id);
        }
        
        if (response) {
            const newTrashState = response.status === 'trash';
            setIsTrashed(newTrashState);
            if (onTrashToggled) {
                onTrashToggled(newTrashState);
            }
        }
    };

    const handleTogglePromotion = async () => {
        if (!currentUser || promotionLoading) return;
        const response = await togglePromotion(toggleFacebookPromotion, post.id);
        if (response && response.success) {
            const newOnFacebook = Boolean(response.on_facebook);
            setOnFacebook(newOnFacebook);
            if (newOnFacebook) {
                setLocalFacebookVoters([]);
                if (onVoteToggled) {
                    onVoteToggled(0);
                }
            }
            if (onPromotionToggled) {
                onPromotionToggled(newOnFacebook);
            }
        }
    };

    const handleAddOrthoCorrection = async (event: FormEvent) => {
        event.preventDefault();
        if (!currentUser || orthoLoading || !orthoInput.trim()) return;
        
        const response = await addOrthoAction(addOrthoCorrection, post.id, orthoInput.trim());
        
        if (response && response.success) {
            const newOrthoCorrection = orthoInput.trim();
            setOrthoCorrection(newOrthoCorrection);
            setOrthoInput('');
            setShowOrthoInput(false);
            // Notify parent component of the change
            if (onOrthoCorrectionChanged) {
                onOrthoCorrectionChanged(newOrthoCorrection);
            }
        }
    };

    const handleCancelOrthoCorrection = async () => {
        if (!currentUser || orthoLoading) return;
        
        const response = await addOrthoAction(addOrthoCorrection, post.id, '');
        
        if (response && response.success) {
            // Reset form state to adding mode
            setOrthoCorrection('');
            setOrthoInput('');
            setShowOrthoInput(false);
            // Notify parent component of the change
            if (onOrthoCorrectionChanged) {
                onOrthoCorrectionChanged('');
            }
        }
    };

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            title="Modération"
        >
            <div className="ModerationDrawer-content">
                <div className="ModerationDrawer-postInfo">
                    <div className="ModerationDrawer-postInfo-header">
                        <div>
                            <h3 dangerouslySetInnerHTML={{__html: post.title.rendered}} />
                            <p>Par {post.author_name}</p>
                        </div>
                        <CopyLinkButton link={post.link} />
                    </div>
                </div>
                
                <div className="ModerationDrawer-actions">
                    {!onFacebook && (
                        <button 
                            className={`ModerationDrawer-action facebook-vote ${voteLoading ? 'loading' : ''}`}
                            onClick={handleVoteForFacebook}
                            disabled={voteLoading}
                        >
                            <div className="action-main">
                                <span className="action-text">
                                    {voteLoading ? 'Traitement...' : 
                                     currentUserHasVoted ? 'Retirer mon vote facebook' : 'Voter pour Facebook'}
                                </span>
                                <span className="voter-count">({voterCount})</span>
                            </div>
                            {voterCount > 0 && (
                                <div className="voter-list">
                                    {localFacebookVoters.map(v => v.displayName || String(v.ID)).join(', ')}
                                </div>
                            )}
                        </button>
                    )}

                    {(onFacebook || voterCount > 0) && (
                        <button 
                            className={`ModerationDrawer-action ${promotionLoading ? 'loading' : ''}`}
                            onClick={handleTogglePromotion}
                            disabled={promotionLoading}
                        >
                            {promotionLoading ? 'Traitement...' : (onFacebook ? 'Retirer de Facebook' : 'Promouvoir sur Facebook')}
                        </button>
                    )}
                    
                    {hasOrthoCorrection() ? (
                        <button 
                            className="ModerationDrawer-action ortho-correction"
                            onClick={handleCancelOrthoCorrection}
                            disabled={orthoLoading}
                        >
                            <span className="action-text">Annuler correction orthographique</span>
                            <span className="action-hint">
                                {orthoLoading ? 'Traitement...' : `"${orthoCorrection}"`}
                            </span>
                        </button>
                    ) : (
                        <>
                            <button 
                                className="ModerationDrawer-action"
                                onClick={() => setShowOrthoInput(!showOrthoInput)}
                            >
                                {showOrthoInput ? 'Annuler demande de correction' : 'Demander une correction'}
                            </button>

                            {showOrthoInput && (
                                <form className="ModerationDrawer-orthoForm" onSubmit={handleAddOrthoCorrection}>
                                    <AutoResizeTextarea
                                        value={orthoInput}
                                        onChange={(e) => setOrthoInput(e.target.value)}
                                        placeholder="Message de correction..."
                                        className="ModerationDrawer-input"
                                        disabled={orthoLoading}
                                        minRows={1}
                                        maxRows={4}
                                    />
                                    <Button 
                                        type="submit" 
                                        variant="primary" 
                                        size="small" 
                                        disabled={!orthoInput.trim()} 
                                        loading={orthoLoading}
                                    >
                                        Envoyer
                                    </Button>
                                </form>
                            )}
                        </>
                    )}
                    
                    <button 
                        className={`ModerationDrawer-action danger ${trashLoading ? 'loading' : ''}`}
                        onClick={handleMoveToTrash}
                        disabled={trashLoading}
                    >
                        {trashLoading ? 'Traitement...' : (isTrashed ? 'Retirer de la corbeille' : 'Mettre à la corbeille')}
                    </button>

                    
                </div>
            </div>
        </Drawer>
    );
};

export default ModerationDrawer;
